/**
 * «Голосовой стенд» — `GET /api/voice-lab/options` (модели с наличием ключа, режимы, салоны) и
 * `POST /api/voice-lab/token` (комната LiveKit с агентом `prokat-voice-lab`). Агент шлёт ход разговора
 * data-сообщениями topic `lab`. Спека `docs/specs/2026-10-26-voice-lab-design.md`.
 */
import { apiFetch } from "@/lib/api";

export type LabModel = {
  code: string;
  label: string;
  available: boolean;
  voice: string;
  voices: string[];
};
/** `phones` — кому стенд может позвонить по телефону (пусто — телефония не настроена). */
export type LabOptions = {
  models: LabModel[];
  modes: string[];
  locations: { id: number; name: string; short: string }[];
  phones: string[];
};
export type LabRequest = {
  model: string;
  mode: string;
  name: string;
  location_id: number;
  request_date: string;
  voice: string;
  /** скорость речи OpenAI (`speed` сессии, 0,8–1,3); у других моделей не передаём */
  speed?: number;
  /** что искала — робот говорит «Вы у нас искали …» */
  interest: string;
  /** номер клиентки для действий в CRM (запись, перезвон, отказ); пусто — номер звонка, если мобильный */
  crm_phone?: string;
  /** OpenAI: кто решает конец реплики и перебивания — модель или LiveKit */
  turn?: "server" | "livekit";
};
export const SPEEDS = [1.0, 1.05, 1.1, 1.2];
export const INTERESTS = [
  "свадебное платье",
  "вечернее платье",
  "выпускное платье",
  "платье",
  "мужской костюм",
];
export type LabToken = {
  url: string;
  token: string;
  room: string;
  model: string;
  mode: string;
};

/** Расход разговора: токены по видам × цена за 1M; `usd` = null — цены модели нет (`voicelab/price.py`). */
export type LabCost = {
  rows: { what: string; tokens: number | null; usd: number | null }[];
  usd: number | null;
  seconds: number;
  source: string | null;
};

/** Событие агента: реплика (пауза — мс от конца речи человека до начала ответа, на сервере), инструмент,
 * расход с начала разговора (после каждого ответа модели), конец с итоговым расходом. */
export type LabEvent =
  | { type: "hello"; model: string; mode: string }
  | {
      type: "turn";
      who: "user" | "agent";
      text: string;
      latency_ms: number | null;
      at: string;
      t?: number | null;
      interrupted?: boolean;
      /** реплика собеседника — эхо слов робота в трубке (ложное перебивание) */
      echo?: boolean;
      /** реплика робота прозвучала готовой записью из библиотеки фраз — код фразы */
      phrase?: string | null;
      /** карточка из журнала: что на самом деле прозвучало в записи (GigaAM) за эту реплику; null — не нашли */
      heard?: string | null;
      /** секунда записи, где эта фраза началась — перемотка к ней, а не к концу реплики `t` */
      heard_start?: number | null;
    }
  | {
      type: "tool";
      name: string;
      args: Record<string, unknown>;
      result: string;
      ms: number;
      at: string;
      t?: number | null;
      /** find_answer / search_knowledge: карточки базы знаний за ответом (`used` — прошла порог и ушла модели;
       * `score` — сходство, у восстановленных по тексту нет) */
      cards?: LabKbCard[];
      /** find_answer: эмбеддинг вопроса в OpenRouter — токены, $ и мс */
      embed?: { tokens?: number | null; usd?: number | null; ms: number; error?: boolean };
      /** ответ из кэша разговора (тот же вопрос второй раз) — без нового эмбеддинга */
      cached?: boolean;
      /** инструмент не уложился в срок или упал: «превышен таймаут 15 с»; `result` — заглушка, ушедшая модели */
      error?: string;
      /** звонок до 27.10.26: карточки узнаны по заголовкам в тексте ответа */
      restored?: boolean;
      /** free_slots: салон, по которому смотрели часы (с 03.11.26; раньше — салон карточки) */
      location_id?: number | null;
    }
  | { type: "cost"; cost: LabCost | null }
  | { type: "end"; outcome: string; cost?: LabCost | null };

export const labOptionsQuery = () => ({
  queryKey: ["voice-lab-options"],
  queryFn: () => apiFetch<LabOptions>("/api/voice-lab/options"),
  staleTime: 60_000,
});

export const labToken = (body: LabRequest) =>
  apiFetch<LabToken>("/api/voice-lab/token", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const labCall = (body: LabRequest & { phone: string }) =>
  apiFetch<{ room: string; phone: string }>("/api/voice-lab/call", {
    method: "POST",
    body: JSON.stringify(body),
  });
/** Карточка сделки для звонка робота (`GET /api/voice-lab/deal/{id}`): `name` — проверенное имя (пусто —
 * в карточке заглушка, робот не называет по имени), `blockers` — почему звонить нельзя. */
export type LabDealCard = {
  deal_id: number;
  title: string;
  stage: string;
  name_raw: string;
  name: string;
  phone: number | null;
  location_id: number | null;
  location_name: string | null;
  event_date: string | null;
  request_date: string;
  next_work_now: string | null;
  calls: number;
  next_work: { at: string; days: number; rule: string };
  blockers: string[];
};
export const labDealQuery = (id: number | null) => ({
  queryKey: ["voice-lab-deal", id],
  queryFn: () => apiFetch<LabDealCard>(`/api/voice-lab/deal/${id}`),
  enabled: id != null,
  retry: false,
});
export const labDealCall = (
  id: number,
  body: Pick<LabRequest, "model" | "mode" | "voice" | "speed" | "turn">,
) =>
  apiFetch<{ room: string; phone: string; deal_id: number }>(
    `/api/voice-lab/deal/${id}/call`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

/** Журнал действий звонка (`GET /api/voice-lab/log`): события робота и записи в Bitrix с параметрами. */
export type LabLogEvent = {
  id: number;
  room: string;
  deal_id: number | null;
  kind: "start" | "turn" | "tool" | "crm" | "next_work" | "end";
  name: string;
  params: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  error: string | null;
  ms: number | null;
  date: string;
};
export type LabLogBitrix = {
  id: number;
  room: string;
  method: string;
  entity: string;
  entity_id: number;
  params: Record<string, unknown>;
  before: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  error: string | null;
  reason: string;
  date: string;
};
export const labLogQuery = (room: string) => ({
  queryKey: ["voice-lab-log", room],
  queryFn: () =>
    apiFetch<{
      rooms: string[];
      events: LabLogEvent[];
      bitrix: LabLogBitrix[];
    }>(`/api/voice-lab/log?room=${room}`),
});

/** Идёт ли звонок: комната ещё есть в LiveKit (агент удаляет её в конце разговора). */
export const labCallState = (room: string) =>
  apiFetch<{ room: string; active: boolean }>(`/api/voice-lab/call/${room}`);
/** «Положить трубку»: удалить комнату звонка. */
export const labHangup = (room: string) =>
  apiFetch<{ room: string; ended: boolean }>(`/api/voice-lab/call/${room}`, {
    method: "DELETE",
  });

export type LabKbCard = {
  id: number;
  title: string | null;
  score?: number | null;
  used?: boolean;
  rev?: number;
};

/** Строка журнала разговоров (`GET /api/voice-lab/calls`), новые сверху. */
export type LabCallRow = {
  room: string;
  started: string;
  seconds: number | null;
  model: string;
  voice: string;
  speed: number | null;
  mode: string;
  name: string | null;
  /** салон карточки звонка (id CRM); робот мог сменить его `set_city` — у шага free_slots свой `location_id` */
  location_id?: number | null;
  location_name: string | null;
  phone: string | null;
  outcome: string;
  turns: number;
  latency_avg: number | null;
  usd: number | null;
  audio: boolean;
  /** кто закончил: finish (робот) / hangup (собеседник) / limit / no_answer; null — разговоры до 26.10.26 */
  end_reason: string | null;
  /** версия инструкции — 8 знаков sha1 текста; null — разговоры до 27.10.26 */
  prompt?: string | null;
  /** кто решал конец реплики и перебивания: server (модель) / livekit */
  turn?: string;
  /** сделка звонка: карточка звонка по сделке или шаг CRM; null — не нашли */
  deal_id?: number | null;
  /** замечания к звонку по статусам — только в списке журнала */
  remarks?: Record<string, number>;
  /** шагов инструментов с ошибкой (таймаут, сбой) — модель получила заглушку */
  errors?: number;
  /** поиск в LightRAG (`search_knowledge`): сколько раз и сколько без ответа */
  lightrag?: { n: number; empty: number };
  /** канал разговора и линия сценария (`voice/obzvon`, `chat/obzvon`; «общая до 03.11.26» у старых меток) */
  channel?: "voice" | "chat";
  line?: string | null;
  /** линия и версия (`voice/obzvon/v4`): у звонков до 03.11.26 — по старой метке `v4:…`; у бота тех дней — null */
  version_key?: string | null;
};

/** Что прозвучало: расшифровка записи GigaAM по дорожкам, секунды от начала записи (фон `voice_lab_transcribe`). */
export type LabHeard = {
  seconds: number;
  user: { start: number; end: number; text: string }[];
  agent: { start: number; end: number; text: string }[];
};

/** Задача «прислать ссылку на самозапись»: wait → work → sent | preview | no_chat | error; has_link. */
export type LabLinkSend = {
  id: number;
  phone: string;
  channel: string;
  status: string;
  chat_id: number | null;
  text: string | null;
  error: string | null;
  date_send: string | null;
};

/** Задача коллеге из звонка робота: не смог ответить (`no_answer`) или жалоба (`complaint`). */
export type CallProblem = {
  id: number;
  kind: string;
  summary: string;
  phone: string | null;
  context: { who: string; text: string }[];
  status: "new" | "done";
  comment: string | null;
  room: string | null;
  location_id: number | null;
  user_id: number | null;
  date: string | null;
  date_done: string | null;
};

/** Пункт чек-листа обращения (`voicelab/checklist`): «узнать» (fact), «сказать» (say), «сделать в системе»
 * (do). `state`: done — засчитан, pending — черновик «Чатов» с ним ждёт отправки, none — ещё нет. */
export type LabChecklistItem = {
  code: string;
  title: string;
  short: string;
  kind: "fact" | "say" | "do";
  state: "done" | "pending" | "none";
  value: string;
  source: "model" | "code" | null;
  /** реплика робота в журнале бота (с нуля); null — «Чаты» */
  turn: number | null;
  /** черновик «Чатов», которым отмечено; null — засчитано сразу (бот, действие CRM) */
  suggest_id: number | null;
  at: string | null;
};

/** Чек-лист обращения: последний у разговора бота или чата «Чатов». Спека 2026-11-04-ai-checklists-design. */
export type LabChecklist = {
  type: string;
  version: string;
  title: string;
  /** начало обращения «Чатов» («2026-11-03 12:00»); null — разговор бота */
  since: string | null;
  items: LabChecklistItem[];
  /** все отметки по порядку; dropped — черновик отклонён, устарел или не вышел */
  marks: {
    code: string;
    value: string;
    source: "model" | "code";
    state: "done" | "pending" | "dropped";
    turn: number | null;
    suggest_id: number | null;
    at: string;
  }[];
};

/** Карточка разговора: всё из журнала + `row` (сводка как в списке). Запись — `GET …/calls/{room}/audio`. */
export type LabCallCard = {
  room: string;
  turns: Extract<LabEvent, { type: "turn" }>[];
  tools: Extract<LabEvent, { type: "tool" }>[];
  cost: LabCost | null;
  audio: boolean;
  row: LabCallRow;
  heard: LabHeard | null;
  /** фразы записи, которым в журнале реплики нет: сказано, а модель не записала */
  heard_extra?: { who: "user" | "agent"; start: number; end: number; text: string }[];
  /** курс доллара для рублей на экране (ЦБ на день; `date` null — запасной курс) */
  rub?: { rate: number; date: string | null; source: string };
  /** ссылки на самозапись, которые робот пообещал прислать (`zapis_link_send`) */
  links?: LabLinkSend[];
  /** вопросы, которые робот передал коллеге (`report_problem`), — «Телефония» → «Задачи робота» */
  problems?: CallProblem[];
  end_t: number | null;
  /** текст инструкции, ушедший модели; нет — разговоры до 27.10.26 */
  instructions?: string | null;
  /** замечания владельца к звонку, свежие сверху */
  remarks?: LabRemark[];
  /** что робот сделал в CRM, человеческим языком (`voicelab/actions.py`) */
  actions?: LabAction[];
  /** сделка звонка: стадия сейчас по своей копии и чат клиента для панели «чат + сделка» */
  deal?: { id: number; stage: string | null; stage_name: string | null; chat_id: number | null } | null;
  /** лог звонка на АТС voip-2 (`voicelab/pbx.py`); null — ещё не снят (`voice-lab-pbx`) */
  pbx?: LabPbx | null;
  /** чек-лист обращения разговора бота; null — у разговора его нет */
  checklist?: LabChecklist | null;
};

/** Что видела АТС: гудки, ответ сети, «занято», кто первым положил трубку, строки `full` лога Asterisk. */
export type LabPbx = {
  room: string;
  phone: string;
  fetched: string;
  call_id: string | null;
  outcome: "answered" | "no_answer" | "busy" | "congestion" | "cancel" | "unknown" | "missing";
  outcome_label: string;
  trunk?: string | null;
  caller?: string | null;
  started?: string | null;
  ring_s?: number | null;
  talk_s?: number | null;
  hung_up_by?: string | null;
  events: { at: string; what: string; kind: string }[];
  raw?: string[];
};

/** Замечание к звонку: к реплике (`turn`) или к секунде записи (`t`). Статусы ставит модель в чате. */
export type LabRemark = {
  id: number;
  room: string;
  user_id: number;
  turn: number | null;
  t: number | null;
  who: "agent" | "user" | null;
  quote: string;
  text: string;
  transcript: string;
  status:
    | "waiting_voice"
    | "voice_failed"
    | "new"
    | "proposed"
    | "processed"
    | "applied"
    | "noted"
    | "rejected";
  status_label: string;
  /** канал разговора: voice — звонок или браузер, chat — переписка бота (`tg-…`) и шаблоны AI (`tpl-…`) */
  channel?: "voice" | "chat";
  date: string;
  /** ответ владельцу на разбор: что было видно, почему так, что изменили, версия инструкции / коммит */
  answer?: { seen: string; cause: string; change: string; version: string };
  voice: { id: number; status: string; text: string; error: string; seconds: number; url: string } | null;
  /** подпись звонка — только в общем списке */
  call?: LabCallRow | null;
  /** Черновик кнопки AI «Чатов» (`ch-<чат>-<черновик>`): ссылка ведёт в чат. */
  chat_id?: number | null;
};

/** Действие робота в CRM: `status` — applied / pending / preview / no_target / ambiguous / error; у ссылки и
 * передачи коллеге — свой статус. `tool` — номер шага в `tools`: лента показывает действие на его месте. */
export type LabAction = {
  tool: number | null;
  kind: "crm" | "next_work" | "link" | "problem";
  action: string;
  title: string;
  status: string | null;
  status_label: string | null;
  write?: boolean | null;
  plan: string;
  changes: { field: string; before: string; after: string }[];
  deal_id: number | null;
  lead_id: number | null;
  t: number | null;
  at: string | null;
};

export const labRemarksQuery = (status: string, channel = "") => ({
  queryKey: ["voice-lab-remarks", status, channel],
  queryFn: () => {
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    if (channel) q.set("channel", channel);
    return apiFetch<{ remarks: LabRemark[]; statuses: Record<string, string> }>(
      `/api/voice-lab/remarks${q.size ? `?${q}` : ""}`,
    );
  },
});

/** Замечания одной «комнаты»: звонка или версии сценария (`sc-<линия>/<версия>`). */
export const labRoomRemarksQuery = (room: string) => ({
  queryKey: ["voice-lab-remarks", "room", room],
  queryFn: () =>
    apiFetch<{ remarks: LabRemark[] }>(`/api/voice-lab/remarks?${new URLSearchParams({ room })}`),
});

/** Замечание к версии сценария: к выделенному куску текста (`quote`) или к версии целиком. */
export const addScenarioRemark = (
  line: string,
  name: string,
  body: { quote: string; text: string; voice_id?: number | null },
) =>
  apiFetch<LabRemark>(`/api/voice-lab/scenarios/${line}/${name}/remarks`, {
    method: "POST",
    body: JSON.stringify(body),
  });

/** Комната замечаний версии сценария и обратно: `sc-voice/vhod/v2` → линия `voice/vhod`, версия `v2`. */
export const scenarioRoom = (line: string, name: string) => `sc-${line}/${name}`;
export const parseScenarioRoom = (room: string) => {
  const m = /^sc-((?:voice|chat)\/[a-z]+)\/(v\d+)$/.exec(room);
  return m ? { line: m[1]!, name: m[2]! } : null;
};

/** Замечания шаблона AI «Чатов» ко всем его версиям (комнаты `tpl-<id>/v<N>`). */
export const templateRemarksQuery = (id: number) => ({
  queryKey: ["voice-lab-remarks", "template", id],
  queryFn: () =>
    apiFetch<{ remarks: LabRemark[] }>(`/api/voice-lab/remarks?${new URLSearchParams({ template: String(id) })}`),
});

/** Замечание к версии шаблона AI: к выделенному куску (`quote`) или к шаблону целиком. */
export const addTemplateRemark = (
  id: number,
  body: { version: number; quote: string; text: string; voice_id?: number | null },
) =>
  apiFetch<LabRemark>(`/api/voice-lab/ai-templates/${id}/remarks`, { method: "POST", body: JSON.stringify(body) });

/** `tpl-2/v1` → шаблон 2, версия 1. */
export const parseTemplateRoom = (room: string) => {
  const m = /^tpl-(\d+)\/v(\d+)$/.exec(room);
  return m ? { id: Number(m[1]), version: Number(m[2]) } : null;
};

/** Адрес шаблона AI на экране шаблонов; `remarks` — сразу вкладка «Замечания». */
export const templateHref = (id: number, remarks = false) =>
  `/templates?tab=ai&t=${id}${remarks ? "&pane=remarks" : ""}`;

export const addLabRemark = (room: string, body: { turn?: number | null; t?: number | null; text: string; voice_id?: number | null }) =>
  apiFetch<LabRemark>(`/api/voice-lab/calls/${room}/remarks`, { method: "POST", body: JSON.stringify(body) });

export const patchLabRemark = (id: number, text: string) =>
  apiFetch<LabRemark>(`/api/voice-lab/remarks/${id}`, { method: "PATCH", body: JSON.stringify({ text }) });

export const deleteLabRemark = (id: number) =>
  apiFetch<{ id: number }>(`/api/voice-lab/remarks/${id}`, { method: "DELETE" });

export const labCallsQuery = () => ({
  queryKey: ["voice-lab-calls"],
  queryFn: () =>
    apiFetch<{ from: string; to: string; calls: LabCallRow[]; rub?: { rate: number } }>(
      "/api/voice-lab/calls",
    ),
});

export const labCallQuery = (room: string) => ({
  queryKey: ["voice-lab-call", room],
  queryFn: () => apiFetch<LabCallCard>(`/api/voice-lab/calls/${room}`),
});

export const labAudioUrl = (room: string) =>
  `/api/voice-lab/calls/${room}/audio`;
export const labMp3Url = (room: string) =>
  `/api/voice-lab/calls/${room}/audio.mp3`;

export const MODES: Record<string, string> = {
  faq: "Сценарий + частые ответы и справки",
  script: "Сценарий",
  kb: "Сценарий + база знаний (поиск)",
};
export const OUTCOMES: Record<string, string> = {
  booked: "записан",
  callback: "перезвонить",
  link: "ссылка на запись",
  problem: "передан коллеге",
  not_actual: "не актуально",
  do_not_call: "просит не звонить",
  voicemail: "автоответчик",
  other: "прочее",
  interrupted: "прерван",
  no_answer: "не ответили",
  busy: "занято",
  rejected: "отклонили",
  dial_failed: "не дозвонились",
  transfer: "переведён на оператора",
  error: "сбой",
};
export const END_REASONS: Record<string, string> = {
  finish: "робот закончил разговор",
  hangup: "собеседник положил трубку / закрыл страницу",
  limit: "вышло время (10 мин)",
  no_answer: "не взяли трубку",
  dial: "звонок не состоялся",
  transfer: "переведён на оператора",
  model_error: "модель отказала",
  closed: "разговор закрыт (сбой или «Положить трубку»)",
  startup: "робот не смог начать (ключ, модель)",
  voicemail: "ответил автоответчик — трубку положили",
};

/** Образец голоса (`GET /api/voice-lab/voices`): одна фраза робота этим голосом; `model` — код модели стенда. */
export type LabVoiceSample = {
  provider: "openai" | "google" | "yandex";
  voice: string;
  model: string;
  file: string;
};

export const labVoicesQuery = () => ({
  queryKey: ["voice-lab-voices"],
  queryFn: () =>
    apiFetch<{
      phrase: string;
      voices: LabVoiceSample[];
      marin: {
        phrase: string;
        samples: { file: string; speed: number; brand: string }[];
      };
    }>("/api/voice-lab/voices"),
  staleTime: 5 * 60_000,
});

export const labVoiceUrl = (file: string) => `/api/voice-lab/voices/${file}`;

/** Инструмент агента: `модели` — описание функции, которое видит модель; `код` — что делает код на стенде. */
export type LabTool = {
  name: string;
  args: string;
  модели: string;
  код: string;
};
/** Вкладка «Инструкция»: текст и инструменты, которые агент отдаст модели при этих настройках формы. */
export type LabInstruction = {
  text: string;
  /** версия в линии (`v6`), линия (`voice/obzvon`) и метка разговора (`voice/obzvon/v6:хэш`) */
  version?: string;
  line?: string;
  label?: string;
  tools: LabTool[];
  other: string[];
};

export const labInstructionQuery = (q: {
  model: string;
  mode: string;
  location_id: number;
  name: string;
  interest: string;
  request_date: string;
  channel?: string;
  /** тип обращения линии: `obzvon` (обзвон заявки), `vhod` (входящий звонок) */
  kind?: string;
}) => ({
  queryKey: ["voice-lab-instruction", q],
  queryFn: () =>
    apiFetch<LabInstruction>(
      `/api/voice-lab/instruction?${new URLSearchParams(Object.entries(q).map(([k, v]) => [k, String(v)]))}`,
    ),
  staleTime: 60_000,
});

/** Линии сценариев робота (вкладка «Сценарии», `GET /api/voice-lab/scenarios`): канал × тип, версии. */
export type LabScenarioVersion = {
  name: string;
  description: string;
  date: string;
  /** замечания, по которым версия сделана: разговор и статус; `room` null — замечания нет в базе */
  remarks: { id: number; room: string | null; status: string | null }[];
  /** sha1 шаблона `vN.md`, 8 знаков, и число строк */
  hash: string;
  lines: number;
  /** разговоров на этой версии за 30 дней (по метке `<линия>/vN:…`) */
  calls_30d: number;
};
export type LabScenarioLine = {
  line: string;
  channel: "voice" | "chat";
  channel_label: string;
  type: string;
  type_label: string;
  current: string;
  calls_7d: number;
  remarks_waiting: number;
  versions: LabScenarioVersion[];
};
export const labScenariosQuery = () => ({
  queryKey: ["voice-lab-scenarios"],
  queryFn: () => apiFetch<{ lines: LabScenarioLine[] }>("/api/voice-lab/scenarios"),
  staleTime: 60_000,
});
/** «Что уйдёт модели» у версии: собранный текст, инструменты, блоки и образец карточки. */
export type LabScenarioAssembled = LabInstruction & {
  blocks: string[];
  sample: { name: string; location_name: string; today: string; mode: string; provider: string };
};
export const labScenarioAssembledQuery = (line: string, name: string) => ({
  queryKey: ["voice-lab-scenario-assembled", line, name],
  queryFn: () => apiFetch<LabScenarioAssembled>(`/api/voice-lab/scenarios/${line}/${name}/assembled`),
  staleTime: 300_000,
});
export const labScenarioQuery = (line: string, name: string) => ({
  queryKey: ["voice-lab-scenario", line, name],
  queryFn: () =>
    apiFetch<{ line: string; name: string; text: string; diff: string }>(`/api/voice-lab/scenarios/${line}/${name}`),
  staleTime: 300_000,
});

/** Подбор товара (вкладка «Подбор», инструмент робота `find_products`) — спека `2026-10-27-product-select-design.md`. */
export type SelectAnswer = {
  query_id: number;
  status: "done" | "pending" | "failed";
  mode: string | null;
  say: string;
  complete?: boolean;
  total: number | null;
  filters?: string[];
  lost?: string[];
  unknown?: string[];
  unsupported?: string[];
  ask?: string[];
  facets?: Record<string, [string, number][]>;
  price_from?: number | null;
  /** Уровень цен — медиана цен моделей без крайних (замечание #20); у старых записей журнала поля нет. */
  price_level?: number | null;
  alternatives?: { without: string; total: number }[];
  link?: string | null;
};
export type SelectRow = {
  id: number;
  date: string;
  text: string;
  city: string | null;
  source: string;
  mode: string | null;
  status: SelectAnswer["status"] | "parsed";
  error: string | null;
  result: Omit<SelectAnswer, "query_id" | "status" | "mode"> | null;
  parse_ms: number | null;
  search_ms: number | null;
  cost: number | null;
  verdict: "ok" | "bad" | null;
  comment: string | null;
};

export const labSelectQuery = () => ({
  queryKey: ["voice-lab-select"],
  queryFn: () => apiFetch<{ rows: SelectRow[] }>("/api/voice-lab/select"),
});

export const labSelect = (body: { text: string; city: string | null }) =>
  apiFetch<SelectAnswer>("/api/voice-lab/select", { method: "POST", body: JSON.stringify(body) });

export const labSelectVerdict = (id: number, body: { verdict: "ok" | "bad" | null; comment: string | null }) =>
  apiFetch<{ id: number }>(`/api/voice-lab/select/${id}/verdict`, { method: "POST", body: JSON.stringify(body) });

/** Популярный запрос Wordstat и итог его прогона через подбор (`select-wordstat`). */
export type SelectKeyword = {
  id: number;
  rank: number;
  text: string;
  count: number;
  noise: string | null;
  query_id: number | null;
  status: SelectRow["status"] | null;
  mode: string | null;
  complete?: boolean | null;
  total?: number | null;
  filters?: string[] | null;
  unknown?: string[] | null;
  unsupported?: string[] | null;
  lost?: string[] | null;
  ask?: string[] | null;
  say?: string | null;
  link?: string | null;
  verdict: "ok" | "bad" | null;
};
export type SelectKeywordGroup = { seed: string; name: string; seeds: string[]; period?: [string, string]; rows: SelectKeyword[] };

export const labSelectKeywordsQuery = () => ({
  queryKey: ["voice-lab-select-keywords"],
  queryFn: () => apiFetch<{ groups: SelectKeywordGroup[] }>("/api/voice-lab/select/keywords"),
});

/** Библиотека готовых фраз робота (`GET /api/voice-lab/phrases`): где в сценарии звучит, запись, что услышал GigaAM. */
export type LabPhraseTake = { file: string; take: number; heard: string; score: number; brand_ok: boolean | null; at: string };
export type LabPhrase = {
  code: string;
  text: string;
  step: string;
  when: string;
  by: "agent" | "model";
  recording: {
    file: string;
    heard: string;
    score: number;
    brand_ok: boolean | null;
    ok: boolean;
    attempts?: number;
    model?: string;
    voice: string;
    speed?: number;
    at?: string;
    chosen_take?: number;
    chosen_at?: string;
  } | null;
  takes: LabPhraseTake[];
};

export const labPhrasesQuery = (voice = "marin") => ({
  queryKey: ["voice-lab-phrases", voice],
  queryFn: () => apiFetch<{ voice: string; threshold: number; phrases: LabPhrase[] }>(`/api/voice-lab/phrases?voice=${voice}`),
  staleTime: 30_000,
});

export const labPhraseUrl = (voice: string, file: string, ver?: string) =>
  `/api/voice-lab/phrases/${voice}/${file}${ver ? `?v=${encodeURIComponent(ver)}` : ""}`;

export const labPhraseChoose = (code: string, take: number, voice = "marin") =>
  apiFetch(`/api/voice-lab/phrases/${code}/choose?voice=${voice}`, {
    method: "POST",
    body: JSON.stringify({ take }),
  });

/** Что робот говорит чаще всего (`GET /api/voice-lab/phrases/stats`): кандидаты на запись в библиотеку. */
export type LabPhraseStat = {
  text: string;
  n: number;
  calls: number;
  share: number;
  library: string | null;
  played: number;
  variable: boolean;
};

export const labPhraseStatsQuery = (days = 30) => ({
  queryKey: ["voice-lab-phrase-stats", days],
  queryFn: () => apiFetch<{ calls: number; days: number; phrases: LabPhraseStat[] }>(`/api/voice-lab/phrases/stats?days=${days}`),
  staleTime: 60_000,
});
